import { auth } from './firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9910';

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API error ${status}: ${body}`);
  }
}

async function apiFetch(
  path: string,
  idToken: string | null,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (idToken) {
    // Always pull the current token rather than trust the one in
    // component state — Firebase ID tokens expire after 1h and the
    // background refresh isn't 100% reliable on a till that sits open
    // all day with the tab backgrounded. `getIdToken()` returns the
    // cached value when valid and refreshes when expired.
    const fresh = (await auth.currentUser?.getIdToken()) ?? idToken;
    headers.set('Authorization', `Bearer ${fresh}`);
  }
  return fetch(`${API_URL}${path}`, { ...init, headers });
}

async function apiGet<T>(path: string, idToken: string): Promise<T> {
  const res = await apiFetch(path, idToken);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

async function apiSend<T>(
  path: string,
  idToken: string,
  body: unknown,
  method = 'POST',
): Promise<T> {
  const res = await apiFetch(path, idToken, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // The refund endpoint returns a human-readable reason in the body
    // (which line, how much was already refunded); surface it rather
    // than flattening everything to a status code.
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export type WhoAmI = {
  email: string;
  name?: string;
  roles: string[];
};

export function whoami(idToken: string): Promise<WhoAmI> {
  return apiGet<WhoAmI>('/api/v1/auth/whoami', idToken);
}

// ---------------------------------------------------------------------------
// Catalog lookup
// ---------------------------------------------------------------------------

/** Which step of the server-side cascade matched. Drives the little
 *  chip on each result row so the cashier can see *why* it matched. */
export type PosLookupKind = 'sku' | 'upc' | 'name' | 'none';

/** One catalog hit. `price` and `cost` are integer cents — pass them
 *  straight to the money helpers, never multiply by 100. */
export type PosHit = {
  id: string;
  /** Canonical tenant slug ("atlanticgrowshop" | "backroom" |
   *  "frontroom"). Item ids are only unique *within* a tenant, so cart
   *  identity is `tenant + id` — see `lineKey` in `pos/money.ts`. */
  tenant: string;
  /** Human label for the shop chip ("Grow Shop", "Back Room", …). */
  tenant_label: string;
  name: string;
  brand?: string | null;
  price: number | null;
  cost: number | null;
  upc?: string | null;
  /** Computed display SKU (`G` + first 6 of id, uppercased). */
  sku: string;
  image_id?: string | null;
  /** Bridgewater count only. Advisory — zero doesn't block a sale. */
  stock: number;
  /** Sells at 0% HST. Set from the item's `flags.taxExempt`; the same
   *  flag is why the item carries no tax in Square. */
  tax_exempt: boolean;
};

export type PosLookupResponse = {
  kind: PosLookupKind;
  hits: PosHit[];
};

/** Catalog lookup across the three sellable tenants (grow shop, back
 *  room, front room — never the florist archive). Server cascades SKU
 *  exact → UPC exact → free-text name; the first step with hits wins,
 *  and the name step is limited *per tenant* so a match-heavy catalog
 *  can't crowd the others out. Hidden and deleted items never come
 *  back. */
export function posLookup(
  idToken: string,
  q: string,
  limit?: number,
): Promise<PosLookupResponse> {
  const params = new URLSearchParams({ q });
  if (limit != null) params.set('limit', String(limit));
  return apiGet<PosLookupResponse>(
    `/api/v1/pos/lookup?${params.toString()}`,
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Register settings
// ---------------------------------------------------------------------------

export type PosSettings = {
  /** How many days back the till may browse (and, from R2, refund).
   *  `0` means no limit. Server-enforced as well — the UI clamp is
   *  convenience, not a control. */
  refund_window_days: number;
  /** True when this server records sales as test data. Set in server
   *  config, never here — the till renders test chrome from it but has
   *  no way to change it. */
  test_mode: boolean;
};

export function posSettings(idToken: string): Promise<PosSettings> {
  return apiGet<PosSettings>('/api/v1/pos/settings', idToken);
}

// ---------------------------------------------------------------------------
// Recording sales
// ---------------------------------------------------------------------------

/** One line of a sale being recorded.
 *
 *  A catalog line sends `tenant` + `item_id` and **no price** — the
 *  server prices it from the catalog, so nothing here can undercharge.
 *  A custom amount sends neither, plus `unit_price_cents`, because a
 *  manually-entered amount has no catalog price to check against. */
export type SaleLineRequest = {
  tenant?: string;
  item_id?: string;
  name?: string;
  quantity: number;
  unit_price_cents?: number;
};

/** Tender on a sale. `none` is the owner-withdrawal case — nothing was
 *  owed, so nothing was tendered. Deliberately distinct from `other`,
 *  which claims money arrived by some route we didn't enumerate. The
 *  server rejects `none` on any basis but `owner`. */
export type SaleTender = RefundTender | 'none';

export type SaleRequest = {
  /** Client-generated UUID, and the idempotency key: a retry after a
   *  lost response returns the original sale instead of recording a
   *  second one. Generate it once per sale, not per attempt. */
  id: string;
  tender: SaleTender;
  price_basis: PriceBasisWire;
  lines: SaleLineRequest[];

  /** The cashier's answer to the receipt prompt. */
  print_receipt?: boolean;

  /** Which till is asking — see `pos/station.ts`. Receipts are routed
   *  by it; a sale without one records exactly the same and prints
   *  nowhere. */
  station_id?: string;

  /** Cash only, and for the paper alone: the server records the sale's
   *  total either way. Sent so the printed receipt can show the change
   *  the customer was handed. */
  tendered_cents?: number;
  change_cents?: number;
};

export type PriceBasisWire = 'retail' | 'employee' | 'owner';

export type SaleResponse = {
  id: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  /** True when the server already had this id — a retry, not a second
   *  sale. Success either way. */
  already_recorded: boolean;
  is_test: boolean;
  /** True when a receipt was asked for and a connected printer took it.
   *  False means no paper came out — printing off, no station id, or
   *  the counter's print service is down. The sale is recorded
   *  regardless; this only decides what the summary says. */
  receipt_printed: boolean;
};

export function posCreateSale(
  idToken: string,
  body: SaleRequest,
): Promise<SaleResponse> {
  return apiSend<SaleResponse>('/api/v1/pos/sales', idToken, body);
}

// ---------------------------------------------------------------------------
// Reprinting a receipt
// ---------------------------------------------------------------------------

export type ReprintResponse = {
  /** Whether a connected printer took it. Same meaning as on a sale. */
  receipt_printed: boolean;
  total_cents: number;
};

/** Print a receipt for a sale that is already recorded.
 *
 *  **Sends no money and no lines** — the server rebuilds the receipt
 *  from the stored sale, so a reprint can't say anything the ledger
 *  doesn't. The slip is marked REPRINT, carries the original date, and
 *  omits cash tendered/change, which were never stored. */
export function posReprintReceipt(
  idToken: string,
  body: {
    vendor: string;
    location: string;
    transaction_id: string;
    station_id?: string;
  },
): Promise<ReprintResponse> {
  return apiSend<ReprintResponse>('/api/v1/pos/receipts', idToken, body);
}

// ---------------------------------------------------------------------------
// Printer stations
// ---------------------------------------------------------------------------

/** One station this till could send receipts to. */
export type PosPrintStation = {
  station_id: string;
  /** Named in the server's `[print].stations`. A connected station that
   *  isn't configured still works, but usually means a typo in some
   *  counter's config — so the settings screen says so. */
  configured: boolean;
  /** A print service is holding a websocket open for it right now. */
  connected: boolean;
  /** What that service prints on, e.g. "winspool: EPSON TM-T88V". */
  printer?: string;
  /** Whether the printer answered its last liveness probe. */
  printer_ready?: boolean;
};

export type PosPrintStationsResponse = {
  /** False when the server has printing switched off entirely — worth
   *  saying plainly rather than showing an empty list that reads as
   *  "every printer is off". */
  enabled: boolean;
  stations: PosPrintStation[];
};

/** Which printer stations exist, and which are switched on right now.
 *
 *  Feeds the Settings tab's dropdown so nobody has to type a station id
 *  that must match the server's config and a counter PC's config
 *  exactly. */
export function posPrintStations(
  idToken: string,
): Promise<PosPrintStationsResponse> {
  return apiGet<PosPrintStationsResponse>(
    '/api/v1/pos/print-stations',
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Past transactions (R1 — read-only)
// ---------------------------------------------------------------------------

/** One line of a past sale. All money is integer cents. */
export type PosTxnLine = {
  /** Ordinal position in the sale, 0-based. **This is the refund key** —
   *  not the row's database pk (reassigned by the transaction-repair
   *  path) and not `line_item_id` (empty-string and non-unique on
   *  legacy rows). */
  line_index: number;
  line_item_id?: string | null;
  /** Vendor catalog id. Absent on ~11.5% of historical lines. */
  item_id?: string | null;
  name: string | null;
  quantity: number;
  /** Per-unit, integer cents. */
  unit_price: number;
  /** `unit_price × quantity`, integer cents — precomputed server-side
   *  so the UI never re-derives money. */
  extended: number;
  /** Already refunded, summed from the refund ledger. */
  refunded_quantity: number;
  /** True when the item behind the line is tax exempt and was rung up
   *  with no HST. Drives the refund preview so the cashier counts out
   *  the same money the server records. */
  tax_exempt: boolean;
  /** `quantity - refunded_quantity`, clamped at 0. Bounds the stepper. */
  refundable_quantity: number;
};

export type PosTransaction = {
  vendor: string;
  location: string;
  id: string;
  /** RFC3339. */
  created_at: string | null;
  subtotal: number | null;
  total: number | null;
  /** `total - subtotal`. Per-line tax is NULL on every stored row, so
   *  this is the only tax figure available. */
  tax: number | null;
  /** `'retail'` | `'employee'` — how the sale was priced. Absent on
   *  Square rows, which have no equivalent and are retail by
   *  definition. */
  price_basis?: string | null;
  /** Who rang it up. Register sales only. */
  cashier?: string | null;
  line_items: PosTxnLine[];
};

export type PosTransactionsResponse = {
  day: string;
  within_window: boolean;
  count: number;
  /** Sum of `total` across the day, integer cents. */
  gross: number;
  /** Newest first. */
  transactions: PosTransaction[];
};

/** A day of past sales. `day` is `YYYY-MM-DD`, bucketed UTC
 *  midnight-to-midnight to match the admin transactions page.
 *  Throws `ApiError` 403 when the day falls outside the refund window. */
export function posTransactions(
  idToken: string,
  day: string,
): Promise<PosTransactionsResponse> {
  const params = new URLSearchParams({ day });
  return apiGet<PosTransactionsResponse>(
    `/api/v1/pos/transactions?${params.toString()}`,
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Refunds (R2)
// ---------------------------------------------------------------------------

/** Fixed vocabulary, mirrored server-side. Free text goes in `note`. */
export const REFUND_REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'changed_mind', label: 'Changed mind' },
  { value: 'price_adjustment', label: 'Price adjustment' },
  { value: 'other', label: 'Other' },
] as const;

export type RefundTender = 'cash' | 'card' | 'other';

/** Tender on a refund. `none` is the owner-withdrawal reversal: the
 *  goods go back and the recorded cost and HST come off, but no cash
 *  moves — none moved when the withdrawal was rung up either. The
 *  server accepts it only when the sale really was a withdrawal. */
export type RefundTenderWire = SaleTender;

export type RefundLineRequest = {
  line_index: number;
  quantity: number;
  restock: boolean;
};

export type RefundResponse = {
  refund_id: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  /** Whether a connected printer took the refund slip. See
   *  `SaleResponse.receipt_printed`. */
  receipt_printed: boolean;
};

/** Record a refund against a past sale.
 *
 *  **Deliberately sends no money.** Unit prices come from the stored
 *  sale server-side, so the totals here are computed from what was
 *  actually charged — which is also what makes an employee-priced sale
 *  refund at the right amount. A 409 means someone else refunded those
 *  lines first. */
export function posCreateRefund(
  idToken: string,
  body: {
    vendor: string;
    location: string;
    transaction_id: string;
    tender: RefundTenderWire;
    reason: string;
    note?: string;
    lines: RefundLineRequest[];
    /** Print a refund slip. Unlike a sale, the register doesn't ask —
     *  goods coming back over the counter should leave paper behind on
     *  both sides. */
    print_receipt?: boolean;
    station_id?: string;
  },
): Promise<RefundResponse> {
  return apiSend<RefundResponse>('/api/v1/pos/refunds', idToken, body);
}
